<template>
  <div>
    <ScheduleXCalendar :calendar-app="calendarApp">
      <template #eventModal="{ calendarEvent }">
        <div class="p-6 border">
          <span
            class="flex flex-row justify-between items-start"
            id="dialog-header"
          >
            <span class="flex flex-row gap-4 py-2">
              <div class="h-6 w-6 bg-h-primary-800 rounded-sm mt-2"></div>
              <span class="flex flex-col">
                <h5>
                  {{ calendarEvent.title }}
                </h5>
                <p class="flex flex-row items-center gap-4 text-sm">
                  {{ utils.formatDate(calendarEvent.start) }}
                </p>
              </span>
            </span>
            <Button
              icon="pi pi-trash"
              class="btn-delete"
              v-role="['TEACHER']"
              @click.prevent="confirmDelete(calendarEvent?.id)"
            />
            <button @click.prevent="eventModal.close" class="py-3">
              <v-icon name="io-close-outline" :scale="1.2" />
            </button>
          </span>
          <div>
            <p class="py-3">
              <v-icon name="hi-solid-user-circle" :scale="1.2" />
              {{ calendarEvent.owner }}
            </p>
            <span
              class="pb-6 flex flex-row items-center gap-2"
              :scale="1.2"
              v-if="calendarEvent.videos?.length === 0"
            >
              <div v-if="!isDeprecatedClass(calendarEvent)">
                <div class="flex flex-row items-center gap-2">
                  <v-icon name="bi-camera-video" />
                  <Button
                    class="btn-primary"
                    :label="
                      $t('labels.generic', {
                        pt: 'Junte-se à turma na aula',
                        en: 'Join with class in the meeting',
                      })
                    "
                    @click="goToLiveClass(calendarEvent?.url)"
                  />
                </div>
                <div class="flex flex-row items-center gap-2 py-2">
                  <v-icon
                    name="md-copyall"
                    @click="copyLink(calendarEvent?.url)"
                    class="cursor-pointer"
                  />
                  <p class="text-sm text-h-neutral-700">
                    {{ calendarEvent?.url }}
                  </p>
                </div>
              </div>
              <div v-else class="flex gap-2">
                <v-icon name="bi-calendar-x" />
                <span>
                  {{
                    $t("labels.generic", {
                      pt: "Aula encerrada",
                      en: "Finished class",
                    })
                  }}
                </span>
              </div>
            </span>
            <div
              class="pt-4 max-h-64 overflow-y-auto"
              v-else
              id="list-recorded-lives"
            >
              <Divider align="center" type="solid">
                <h6>
                  {{
                    $t("labels.generic", {
                      pt: "Lista de aulas gravadas",
                      en: "Recorded classes list",
                    })
                  }}
                </h6>
              </Divider>

              <div
                v-for="(video, index) in calendarEvent.videos"
                :key="video?.id"
              >
                <span class="flex flex-col">
                  <span>
                    <h6 class="pt-2">
                      {{
                        $t("labels.generic", {
                          pt: `Vídeo ${index + 1}`,
                          en: `Video ${index + 1}`,
                        })
                      }}
                    </h6>
                    <p class="text-sm pb-2">
                      {{ formatVideoDownloadDate(video.upload_date) }}
                    </p>
                  </span>
                  <Button
                    :id="`download-button-${video.id}`"
                    :label="
                      $t('labels.generic', {
                        pt: `Download `,
                        en: `Download `,
                      })
                    "
                    :loading="btnLoading[index]"
                    icon="pi pi-download"
                    iconPos="right"
                    aria-hidden="true"
                    @click.prevent="
                      downloadClassRecorded(video.video_url, video.id, index)
                    "
                    class="btn-primary"
                  />
                </span>
              </div>
            </div>
            <p v-if="calendarEvent.isPublic">
              <v-icon name="hi-lock-closed" />
            </p>
          </div>
        </div>
      </template>
    </ScheduleXCalendar>
    <ConfirmDialog class="w-[80vw] max-w-[700px]" />
  </div>
</template>
<script lang="ts">
import { defineComponent, reactive } from "vue";
import { AgendaService } from "./agenda.service";
import { QueryParams } from "@/models/query-params.model";
import { AgendaUtils } from "./agenda.util";
import { ToastFactory } from "@/utils/toast-factory.util";
import { Messages } from "@/utils/messages.util";
import { ScheduleXCalendar } from "@schedule-x/vue";
import {
  createCalendar,
  createViewDay,
  createViewMonthAgenda,
  createViewMonthGrid,
  createViewWeek,
  viewMonthGrid,
  CalendarEventExternal,
} from "@schedule-x/calendar";
import "@schedule-x/theme-default/dist/index.css";
import { createEventModalPlugin } from "@schedule-x/event-modal";
import { AgendaMapper } from "./agenda.mapper";
import { AgendaRest } from "@/services/rest/agenda.rest";
import moment from "moment";
import "moment/locale/pt";

export default defineComponent({
  emits: ["setEventEdit", "update:eventCreated"],
  data() {
    return {
      params: {} as QueryParams,
      schedules: [] as Array<any>,
      btnLoading: [] as Array<boolean>,
    };
  },
  props: {
    eventCreated: {
      type: Boolean,
      required: true,
    },
  },
  setup() {
    const range = reactive({ startDate: "", endDate: "" });
    const eventModal = createEventModalPlugin();
    const calendarApp = createCalendar({
      selectedDate: new Date().toISOString().replace("T", " ").slice(0, 16),
      defaultView: viewMonthGrid.name,
      calendars: {
        group: {
          colorName: "group",
          lightColors: {
            main: "#153c59",
            container: "#cde3f4",
            onContainer: "#153c59",
          },
        },
      },
      dayBoundaries: {
        start: "06:00",
        end: "22:00",
      },
      plugins: [eventModal],
      views: [
        createViewDay(),
        createViewWeek(),
        createViewMonthGrid(),
        createViewMonthAgenda(),
      ],
      events: [],
      locale: "pt-BR",
      callbacks: {
        onRangeUpdate({ start, end }) {
          let startDate = new Date(start);
          let endDate = new Date(end);
          startDate.setDate(startDate.getDate() - 1);
          endDate.setDate(endDate.getDate() + 1);
          range.startDate = startDate.toISOString().slice(0, 10);
          range.endDate = endDate.toISOString().slice(0, 10);
        },
      },
    });

    return {
      calendarApp,
      eventModal,
      range,
    };
  },
  components: {
    ScheduleXCalendar,
  },
  methods: {
    confirmDelete(id: string): void {
      this.eventModal.close();
      this.$confirm.require({
        message: this.$t("labels.generic", {
          pt: `O usuário deve estar ciente de que, ao deletar o evento, é possível que o horário não fique mais disponível.`,
          en: `The user should be aware that, upon deleting the event, the time slot may no longer be available.`,
        }),
        header: this.$t("labels.generic", {
          pt: "Deletar evento?",
          en: "Delete event?",
        }),
        acceptLabel: this.$t("labels.generic", {
          pt: "Sim, quero deletar",
          en: "Yes, I want to delete it",
        }),
        rejectLabel: this.$t("labels.generic", {
          pt: "Não, não quero deletar",
          en: "No, I don't want to delete",
        }),
        rejectClass: "btn-outline",
        acceptClass: "btn-delete-block",
        accept: () => {
          this.deleteAgenda(id);
        },
      });
    },
    deleteAgenda(id: string) {
      this.rest.delete(id).subscribe({
        next: () => {
          this.$toast.add(ToastFactory.success(Messages.EVENT_DELETED));
          this.getAllAgenda();
        },
      });
    },
    getAllAgenda(): void {
      this.service.getAllAgenda(this.params);
      this.service.agenda.subscribe({
        next: (res) => {
          this.calendarApp.events.set(
            this.utils.formatEvents(res) as CalendarEventExternal[]
          );
          this.$emit("update:eventCreated", false);
        },
      });
    },
    setRangeDate(): void {
      const oneMonthAgo = new Date();
      oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
      oneMonthAgo.setDate(21);
      const oneMonthAfter = new Date();
      oneMonthAfter.setMonth(oneMonthAfter.getMonth() + 1);
      oneMonthAfter.setDate(7);
      this.params.startDate = oneMonthAgo.toISOString().slice(0, 10);
      this.params.endDate = oneMonthAfter.toISOString().slice(0, 10);
    },
    goToLiveClass(hash: string): void {
      window.open(hash, "_blank");
    },
    isDeprecatedClass(eventCalendar: any): boolean {
      const endDate = new Date(eventCalendar?.end.replace(" ", "T"));
      const today = new Date();
      const isDeprecatedClassroom = today > endDate;
      return isDeprecatedClassroom;
    },
    copyLink(url: string) {
      if (!url) return;

      navigator.clipboard
        .writeText(url)
        .then(() => {
          this.$toast.add(ToastFactory.success(Messages.COPIED_LINK));
        })
        .catch(() => {
          this.$toast.add(ToastFactory.error(Messages.COPY_ERROR_LINK));
        });
    },
    downloadClassRecorded(videoUrl: string, id: string, indexVideo) {
      this.btnLoading[indexVideo] = true;
      this.rest
        .downloadLive(videoUrl)

        .subscribe({
          next: (response) => {
            const blob = new Blob([response]);
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", `video-${id}.mp4`);
            document.body.appendChild(link);
            link.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(link);
            this.btnLoading[indexVideo] = false;
            this.$toast.add(ToastFactory.success(Messages.VIDEO_DOWNLOADED));
          },
          error: () => {
            this.$toast.add(
              ToastFactory.success(Messages.DOWNLOAD_CLASS_ERROR)
            );
            this.btnLoading[indexVideo] = false;
          },
          complete: () => (this.btnLoading[indexVideo] = false),
        });
    },

    formatVideoDownloadDate(date: string): string {
      moment().locale("pt-br");
      return moment(date).format("LLLL");
    },
  },
  computed: {
    service(): AgendaService {
      return new AgendaService();
    },
    utils(): AgendaUtils {
      return new AgendaUtils();
    },
    mapper(): AgendaMapper {
      return new AgendaMapper();
    },
    rest(): AgendaRest {
      return new AgendaRest();
    },
  },
  mounted() {
    if (this.selectedClassId)
      this.params.classroom = this.selectedClassId as string;
    if (this.selectedCourseId)
      this.params.course = this.selectedCourseId as string;
    this.setRangeDate();
    this.getAllAgenda();
  },
  inject: ["selectedCourseId", "selectedClassId"],
  watch: {
    "range.startDate"(value) {
      this.params.startDate = value;
    },
    "range.endDate"(value) {
      this.params.endDate = value;
      this.getAllAgenda();
    },
    selectedCourseId(value) {
      if (value?.length === 0 || value === "") delete this.params.course;
      else this.params.course = value;
      this.getAllAgenda();
    },
    selectedClassId(value) {
      if (value?.length === 0 || value === "") delete this.params.classroom;
      else this.params.classroom = value;
      this.getAllAgenda();
    },
    eventCreated(value) {
      if (value === true) this.getAllAgenda();
    },
  },
});
</script>
